import { useState, useMemo } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  // LayoutDashboard,
  // GraduationCap,
  // BookOpen,
  // FileText,
  Settings,
  Bot,
  Users,
  Database,
  LogOut,
  Cloud,
  FileSearch,
  // FileQuestion,
  Sparkles,
  ChevronDown,
  ChevronUp,
  Presentation,
  FileType,
  Shield,
  History,
  ThumbsUp,
  ClipboardCheck,
  // Presentation,
  // History,
  BookTemplate,
} from 'lucide-react';
import clsx from 'clsx';
import { KeyboardShortcutsPanel } from '../components/KeyboardShortcutsPanel';
import { useAuthStore } from '../store/authStore';
import { hasRoutePermission } from '../config/routePermissions';

interface NavItem {
  name: string;
  path: string;
  icon: React.ElementType;
  children?: NavItem[];
}

const navigation: NavItem[] = [
  // { name: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
  // {
  //   name: 'Content Management',
  //   path: '#',
  //   icon: BookOpen,
  //   children: [
  //     // { name: 'Exams', path: '/exams', icon: GraduationCap },
  //     // { name: 'Courses', path: '/courses', icon: BookOpen },
  //     // { name: 'Tests', path: '/tests', icon: FileCheck },
  //     { name: 'Learning Materials', path: '/materials', icon: FileText },
  //     // { name: 'SharePoint Sync', path: '/sharepoint-sync', icon: Cloud },
  //   ],
  // },
  {
    name: 'Booklet Analysis',
    path: '#',
    icon: FileSearch,
    children: [
      { name: 'Uploaded Booklets', path: '/pdf-analysis', icon: FileSearch },
      { name: 'Portfolios', path: '/pdf-analysis/portfolios', icon: Database },
      { name: 'Question Generator', path: '/pdf-analysis/generate', icon: Sparkles },
      { name: 'Question Bank', path: '/pdf-analysis/question-bank', icon: Database },
      { name: 'SharePoint Sync', path: '/sharepoint-sync', icon: Cloud },
    ],
  },
  {
    name: 'Slides',
    path: '#',
   icon: Presentation,
    children: [
      { name: 'Slides Maker', path: '/slides-maker', icon: Presentation },
      { name: 'Slides History', path: '/slides-history', icon: Presentation},
      { name: 'PPTX Converter', path: '/pptx-converter', icon: FileType },
      { name: 'PPTX History', path: '/pptx-history', icon: History},
    ],
  },
  {
    name: 'Content Assembly',
    path: '/content-assembly',
    icon: BookTemplate,
  },
  {
    name: 'Editorial Review',
    path: '#',
    icon: ClipboardCheck,
    children: [
      { name: 'Review Tasks', path: '/editorial/review-tasks', icon: ClipboardCheck },
      { name: 'Rejection Analytics', path: '/editorial/rejections', icon: History },
      // NWFE-RQ-01.4 admin-only views (permission-gated: ai_review_monitor / prompt_manage)
      { name: 'AI Review Monitor', path: '/editorial/ai-review', icon: Bot },
      { name: 'AI Prompts', path: '/editorial/ai-prompts', icon: Sparkles },
    ],
  },
  {
    name: 'Chatbot',
    path: '#',
    icon: Bot,


    
    children: [
      { name: 'AI Chatbot', path: '/chatbot', icon: Bot },
      { name: 'Chatbot Feedback', path: '/chatbot/feedback', icon: ThumbsUp },
      { name: 'Pinecone Data', path: '/chatbot/pinecone-data', icon: Database },
    ],
  },
  {
    name: 'System Settings',
    path: '#',
    icon: Settings,
    children: [
      { name: 'User Management', path: '/settings/users', icon: Users },
      { name: 'Permission Modules', path: '/settings/permissions', icon: Settings },
      { name: 'Role Management', path: '/settings/role-management', icon: Shield },
      // { name: 'System Config', path: '/settings/system', icon: Settings },
      // { name: 'API Configuration', path: '/settings/api', icon: Settings },
      // { name: 'RAG & Vector DB', path: '/settings/rag', icon: Settings },
    ],
  },
];

export function Layout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  console.log('asdad')

  // Get user permissions
  const userPermissions = user?.permissions || [];

  // Filter navigation based on permissions
  const filteredNavigation = useMemo(() => {
    const filterNavigationByPermissions = (items: NavItem[]): NavItem[] => {
      return items
        .map(item => {
          if (item.children) {
            const visibleChildren = filterNavigationByPermissions(item.children);
            if (visibleChildren.length === 0) return null;
            return { ...item, children: visibleChildren };
          }
          return hasRoutePermission(item.path, userPermissions) ? item : null;
        })
        .filter((item): item is NavItem => item !== null);
    };

    return filterNavigationByPermissions(navigation);
  }, [userPermissions]);

  // State to track which parent sections are expanded
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(['Booklet Analysis', 'System Settings', 'Editorial Review']) // Expanded by default
  );

  
  const toggleSection = (sectionName: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionName)) {
        next.delete(sectionName);
      } else {
        next.add(sectionName);
      }
      return next;
    });
  };

  const isActive = (path: string) => {
    if (path === '#') return false;
    // For routes that have sibling nav entries under the same prefix, match exactly
    if (path === '/pdf-analysis' || path === '/chatbot') {
      return location.pathname === path;
    }
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Get user initials for avatar
  const getUserInitials = () => {
    if (!user) return 'U';
    const nameParts = user.name.split(' ');
    if (nameParts.length >= 2) {
      return `${nameParts[0][0]}${nameParts[1][0]}`.toUpperCase();
    }
    return user.name.substring(0, 2).toUpperCase();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/20 to-orange-50/20">
      {/* Enhanced Sidebar */}
      <div className="fixed inset-y-0 left-0 w-72 bg-gradient-to-b from-white via-white to-blue-50/30 shadow-2xl border-r border-gray-200/50 backdrop-blur-xl">
        <div className="flex flex-col h-full relative overflow-hidden">
          {/* Decorative background pattern */}
          <div className="absolute inset-0 opacity-[0.03] pointer-events-none">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(30,115,190,0.4)_1px,transparent_1px)] bg-[length:24px_24px]" />
          </div>

          {/* Logo Section */}
          <div className="relative flex items-center h-20 px-6 border-b border-gray-200/50 bg-gradient-to-r from-transparent via-blue-50/30 to-orange-50/30">
            <div className="absolute inset-0 bg-gradient-to-r from-[#1e73be]/5 to-[#ea7500]/5" />
            <img
              src="/fk-logo-full.png"
              alt="FK Partners"
              className="h-10 w-auto relative z-10 drop-shadow-sm hover:scale-105 transition-transform duration-300"
            />
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-6 space-y-2 overflow-y-auto relative z-10 custom-scrollbar">
            {filteredNavigation.map((item) => (
              <div key={item.name}>
                {item.children ? (
                  <div className="space-y-1 mb-3">
                    <button
                      onClick={() => toggleSection(item.name)}
                      className="w-full flex items-center justify-between px-4 py-3 text-sm font-extrabold text-gray-700 uppercase tracking-wider hover:bg-gray-50 rounded-xl transition-all duration-200 group"
                    >
                      <div className="flex items-center">
                        <div className="p-1.5 rounded-lg bg-gradient-to-br from-gray-100 to-gray-200 mr-3 shadow-sm group-hover:scale-110 transition-transform duration-200">
                          <item.icon className="w-4 h-4 text-gray-600" />
                        </div>
                        {item.name}
                      </div>
                      {expandedSections.has(item.name) ? (
                        <ChevronUp className="w-4 h-4 text-gray-600" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-gray-600" />
                      )}
                    </button>
                    {expandedSections.has(item.name) && (
                      <div className="ml-3 space-y-1 border-l-2 border-gray-200/50 pl-3 animate-in">
                        {item.children.map((child) => (
                        <Link
                          key={child.path}
                          to={child.path}
                          className={clsx(
                            'group flex items-center px-4 py-3 text-sm font-semibold rounded-xl transition-all duration-300 transform relative overflow-hidden',
                            isActive(child.path)
                              ? 'bg-[#4A90C8] text-white shadow-lg scale-105'
                              : 'text-gray-700 hover:text-[#4A90C8] hover:bg-blue-50 hover:shadow-md hover:scale-102'
                          )}
                        >
                          {isActive(child.path) && (
                            <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent opacity-50" />
                          )}
                          <div className={clsx(
                            'p-2 rounded-lg mr-3 transition-all duration-300 relative z-10',
                            isActive(child.path)
                              ? 'bg-white/20 shadow-inner'
                              : 'bg-transparent group-hover:bg-blue-100 group-hover:scale-110'
                          )}>
                            <child.icon className={clsx(
                              'w-4 h-4 transition-transform duration-300',
                              isActive(child.path) ? 'text-white' : 'text-gray-600 group-hover:text-[#4A90C8]'
                            )} />
                          </div>
                          <span className="relative z-10">{child.name}</span>
                          {isActive(child.path) && (
                            <div className="ml-auto">
                              <div className="w-2 h-2 rounded-full bg-white shadow-lg" />
                            </div>
                          )}
                        </Link>
                      ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    to={item.path}
                    className={clsx(
                      'group flex items-center px-4 py-3.5 text-base font-bold rounded-xl transition-all duration-300 transform relative overflow-hidden mb-1',
                      isActive(item.path)
                        ? 'bg-[#4A90C8] text-white shadow-xl scale-105'
                        : 'text-gray-700 hover:text-[#4A90C8] hover:bg-blue-50 hover:shadow-lg hover:scale-102'
                    )}
                  >
                    {isActive(item.path) && (
                      <>
                        <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent opacity-50" />
                        <div className="absolute left-0 top-0 bottom-0 w-1 bg-white rounded-r-full shadow-lg" />
                      </>
                    )}
                    <div className={clsx(
                      'p-2.5 rounded-xl mr-3.5 transition-all duration-300 relative z-10',
                      isActive(item.path)
                        ? 'bg-white/20 shadow-inner'
                        : 'bg-gradient-to-br from-gray-100 to-gray-200 group-hover:from-blue-100 group-hover:to-blue-200 group-hover:scale-110 group-hover:rotate-6 shadow-sm'
                    )}>
                      <item.icon className={clsx(
                        'w-5 h-5 transition-all duration-300',
                        isActive(item.path) ? 'text-white' : 'text-gray-600 group-hover:text-[#4A90C8]'
                      )} />
                    </div>
                    <span className="relative z-10">{item.name}</span>
                    {isActive(item.path) && (
                      <div className="ml-auto">
                        <div className="w-2.5 h-2.5 rounded-full bg-white shadow-lg animate-pulse" />
                      </div>
                    )}
                  </Link>
                )}
              </div>
            ))}
          </nav>

          {/* Enhanced User Info */}
          <div className="relative px-4 py-4 border-t border-gray-200/50 bg-gradient-to-r from-blue-50/30 to-orange-50/30 backdrop-blur-sm">
            <div className="absolute inset-0 bg-gradient-to-t from-white/50 to-transparent" />
            <div className="flex items-center justify-between relative z-10">
              <div className="flex items-center min-w-0 flex-1 group cursor-pointer hover:bg-white/50 rounded-xl p-2 transition-all duration-300">
                <div className="relative flex-shrink-0">
                  <div className="flex items-center justify-center w-11 h-11 bg-[#4A90C8] rounded-xl shadow-lg group-hover:shadow-xl group-hover:scale-110 transition-all duration-300">
                    <span className="text-sm font-bold text-white">{getUserInitials()}</span>
                  </div>
                  <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-green-500 rounded-full border-2 border-white shadow-sm" />
                </div>
                <div className="ml-3 min-w-0 flex-1">
                  <p className="text-sm font-bold text-gray-900 truncate group-hover:text-[#4A90C8] transition-colors">{user?.name || 'User'}</p>
                  <p className="text-xs text-gray-500 truncate">{user?.email || ''}</p>
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="ml-2 p-2.5 text-gray-400 hover:text-white hover:bg-gradient-to-br hover:from-red-500 hover:to-red-600 rounded-xl transition-all duration-300 flex-shrink-0 shadow-sm hover:shadow-lg hover:scale-110 group"
                title="Logout"
              >
                <LogOut className="w-4 h-4 group-hover:rotate-12 transition-transform duration-300" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="pl-72">
        <main className="p-8">
          <Outlet />
        </main>
      </div>

      {/* Keyboard Shortcuts Panel */}
      <KeyboardShortcutsPanel />
    </div>
  );
}
